import { zodResolver } from '@hookform/resolvers/zod';
import { CheckCircle, XCircle } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router';
import { useResetPassword } from '../hooks/useResetPassword';
import { useValidatePasswordResetToken } from '../hooks/useValidatePasswordResetToken';
import {
  resetPasswordSchema,
  type ResetPasswordFormValues,
} from '../schemas/password-reset-schema';
import { publicPaths } from '@/app/router/paths/publicPaths';
import appIcon from '@/assets/icons/icon.svg';
import { Form, FormControl, FormField, FormItem } from '@/shared/components/ui/form';
import { Input } from '@/shared/components/ui/input';
import { UnifiedButton } from '@/shared/components/ui/unified-button';

export default function NewPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('tokenValidation');

  const { status: tokenStatus, errorState: tokenError } = useValidatePasswordResetToken(token);
  const { resetPassword, isSubmitting, isSuccess, errorState: resetError } = useResetPassword();

  const form = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      token: token || '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  const onSubmit = async (values: ResetPasswordFormValues) => {
    await resetPassword({
      token: token!,
      newPassword: values.newPassword,
      confirmPassword: values.confirmPassword,
    });
  };

  const goToLogin = () => {
    navigate(publicPaths.LOGIN, { state: { passwordResetSuccess: true } });
  };

  const goToRequestReset = () => {
    navigate(publicPaths.FORGOT_PASSWORD);
  };

  const isTokenError = tokenStatus === 'invalid' || tokenStatus === 'error';

  return (
    <div className="w-screen h-screen flex justify-center items-center">
      <div className="flex flex-col justify-center items-center p-0 gap-[2.5rem] w-full max-w-[25rem] px-4">
        <div>
          <img src={appIcon} alt="Reset Password Logo" />
        </div>

        {tokenStatus === 'validating' && (
          <div className="h-40" data-testid="auth-reset-validating" aria-hidden />
        )}

        {isTokenError && (
          <div
            className="flex flex-col items-center gap-[1.5rem] w-full"
            data-testid="auth-reset-token-error"
          >
            <XCircle className="w-16 h-16 text-negative" />
            <div className="text-center">
              <p className="text-light text-base font-medium">
                {tokenError?.title || 'Enlace inválido'}
              </p>
              <p className="text-welcome-subtitle text-sm mt-1">
                {tokenError?.message || 'El enlace de recuperación es inválido o ha expirado.'}
              </p>
            </div>
            <div className="w-full flex flex-col gap-2">
              <UnifiedButton
                buttonType="prominent"
                size="prominent-lg"
                className="w-full"
                onClick={goToRequestReset}
                data-testid="auth-reset-request-new-button"
              >
                Solicitar nuevo enlace
              </UnifiedButton>
              <UnifiedButton
                buttonType="basic"
                size="prominent-lg"
                className="w-full"
                onClick={() => navigate(publicPaths.LOGIN)}
                data-testid="auth-reset-back-button"
              >
                Volver al inicio de sesión
              </UnifiedButton>
            </div>
          </div>
        )}

        {tokenStatus === 'valid' && isSuccess && (
          <div
            className="flex flex-col items-center gap-[1.5rem] w-full"
            data-testid="auth-reset-success"
          >
            <CheckCircle className="w-16 h-16 text-positive" />
            <div className="text-center">
              <p className="text-light text-base font-medium">Contraseña actualizada</p>
              <p className="text-welcome-subtitle text-sm mt-1">
                Inicia sesión con tu nueva contraseña.
              </p>
            </div>
            <UnifiedButton
              buttonType="prominent"
              size="prominent-lg"
              className="w-full"
              onClick={goToLogin}
              data-testid="auth-reset-go-login-button"
            >
              Ir a iniciar sesión
            </UnifiedButton>
          </div>
        )}

        {tokenStatus === 'valid' && !isSuccess && (
          <div className="w-full flex flex-col items-center gap-[1.5rem]">
            <div className="text-center">
              <p className="text-light text-base font-medium">Nueva contraseña</p>
              <p className="text-welcome-subtitle text-sm mt-1">
                Define una nueva contraseña para tu cuenta.
              </p>
            </div>

            {resetError && (
              <div className="w-full p-3 bg-negative/10 border border-negative/30 rounded-md">
                <p className="text-sm text-negative text-center">{resetError.message}</p>
              </div>
            )}

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-6 w-full">
                <FormField
                  control={form.control}
                  name="newPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          placeholder="Nueva contraseña"
                          type="password"
                          {...field}
                          error={form.formState.errors.newPassword?.message}
                          data-testid="auth-reset-new-password-input"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          placeholder="Confirmar contraseña"
                          type="password"
                          {...field}
                          error={form.formState.errors.confirmPassword?.message}
                          data-testid="auth-reset-confirm-password-input"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <div className="flex w-full flex-col gap-2">
                  <UnifiedButton
                    buttonType="prominent"
                    size="prominent-lg"
                    className="w-full"
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="auth-reset-submit-button"
                  >
                    {isSubmitting ? 'Guardando...' : 'Guardar contraseña'}
                  </UnifiedButton>
                  <UnifiedButton
                    buttonType="basic"
                    variant={'outline'}
                    size="prominent-lg"
                    className="w-full"
                    type="button"
                    onClick={() => navigate(publicPaths.LOGIN)}
                    disabled={isSubmitting}
                    data-testid="auth-reset-back-button"
                  >
                    Volver al inicio de sesión
                  </UnifiedButton>
                </div>
              </form>
            </Form>
          </div>
        )}
      </div>
    </div>
  );
}
